import { useState, useEffect } from 'react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Label } from '@paulownia/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@paulownia/shared/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@paulownia/shared/components/ui/tabs';
import { Check, X, Clock, MapPin, Calendar, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  listMarketRequests,
  approveMarketRequest,
  rejectMarketRequest,
} from '@paulownia/shared/services/market-requests';
import { listMarkets } from '@paulownia/shared/services/markets';
import type { MarketRequest, Market, MarketRequestStatus } from '@paulownia/shared/types';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';

interface ApprovalDialogState {
  open: boolean;
  request: MarketRequest | null;
  mode: 'link' | 'create';
  existingMarketId: string;
  newMarketAddress: string;
  newMarketOpeningTime: string;
  newMarketClosingTime: string;
}

const initialApprovalState: ApprovalDialogState = {
  open: false,
  request: null,
  mode: 'create',
  existingMarketId: '',
  newMarketAddress: '',
  newMarketOpeningTime: '07:00',
  newMarketClosingTime: '14:00',
};

const STATUS_LABELS: Record<MarketRequestStatus, string> = {
  pending: 'En attente',
  approved: 'Approuvée',
  rejected: 'Rejetée',
};

const STATUS_COLORS: Record<MarketRequestStatus, 'default' | 'secondary' | 'outline'> = {
  pending: 'default',
  approved: 'secondary',
  rejected: 'outline',
};

export const MarketRequestsAdmin = () => {
  const [requests, setRequests] = useState<MarketRequest[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<MarketRequestStatus | 'all'>('pending');
  const [approvalDialog, setApprovalDialog] = useState<ApprovalDialogState>(initialApprovalState);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; request: MarketRequest | null; reason: string }>({
    open: false,
    request: null,
    reason: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsData, marketsData] = await Promise.all([
        listMarketRequests(),
        listMarkets(),
      ]);
      setRequests(requestsData);
      setMarkets(marketsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = statusFilter === 'all'
    ? requests
    : requests.filter((r) => r.status === statusFilter);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const openApprovalDialog = (request: MarketRequest) => {
    // Find existing markets that match the city and day
    const matchingMarkets = markets.filter(
      (m) => m.city.toLowerCase() === request.city.toLowerCase() && m.dayOfWeek === request.dayOfWeek
    );

    setApprovalDialog({
      open: true,
      request,
      mode: matchingMarkets.length > 0 ? 'link' : 'create',
      existingMarketId: matchingMarkets.length > 0 ? matchingMarkets[0].id : '',
      newMarketAddress: '',
      newMarketOpeningTime: '07:00',
      newMarketClosingTime: '14:00',
    });
  };

  const handleApprove = async () => {
    if (!approvalDialog.request) return;

    const { request, mode, existingMarketId, newMarketAddress, newMarketOpeningTime, newMarketClosingTime } = approvalDialog;

    if (mode === 'link' && !existingMarketId) {
      toast.error('Veuillez sélectionner un marché existant');
      return;
    }

    if (mode === 'create' && !newMarketAddress.trim()) {
      toast.error('Veuillez saisir une adresse pour le nouveau marché');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'link') {
        await approveMarketRequest(request.id, { marketId: existingMarketId });
      } else {
        await approveMarketRequest(request.id, {
          city: request.city,
          dayOfWeek: request.dayOfWeek,
          address: newMarketAddress.trim(),
          openingTime: newMarketOpeningTime,
          closingTime: newMarketClosingTime,
        });
      }

      toast.success('Demande approuvée avec succès');
      setApprovalDialog(initialApprovalState);
      await loadData();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'approbation');
    } finally {
      setLoading(false);
    }
  };

  const openRejectDialog = (request: MarketRequest) => {
    setRejectDialog({
      open: true,
      request,
      reason: '',
    });
  };

  const handleReject = async () => {
    if (!rejectDialog.request) return;

    setLoading(true);
    try {
      await rejectMarketRequest(rejectDialog.request.id, rejectDialog.reason || undefined);
      toast.success('Demande rejetée');
      setRejectDialog({ open: false, request: null, reason: '' });
      await loadData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors du rejet');
    } finally {
      setLoading(false);
    }
  };

  // Get matching markets for approval dialog
  const getMatchingMarkets = () => {
    if (!approvalDialog.request) return [];
    return markets.filter(
      (m) =>
        m.city.toLowerCase() === approvalDialog.request!.city.toLowerCase() &&
        m.dayOfWeek === approvalDialog.request!.dayOfWeek
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Demandes de marchés</h2>
          {pendingCount > 0 && (
            <Badge variant="default" className="bg-orange-500">
              {pendingCount} en attente
            </Badge>
          )}
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          Actualiser
        </Button>
      </div>

      {/* Status Filter Tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as MarketRequestStatus | 'all')}>
        <TabsList>
          <TabsTrigger value="pending">
            En attente ({requests.filter((r) => r.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approuvées ({requests.filter((r) => r.status === 'approved').length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejetées ({requests.filter((r) => r.status === 'rejected').length})
          </TabsTrigger>
          <TabsTrigger value="all">
            Toutes ({requests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={statusFilter} className="mt-4">
          {filteredRequests.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  {statusFilter === 'pending'
                    ? 'Aucune demande en attente'
                    : statusFilter === 'approved'
                      ? 'Aucune demande approuvée'
                      : statusFilter === 'rejected'
                        ? 'Aucune demande rejetée'
                        : 'Aucune demande'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredRequests.map((request) => (
                <Card key={request.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {request.city}
                      </CardTitle>
                      <Badge variant={STATUS_COLORS[request.status]}>
                        {STATUS_LABELS[request.status]}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>{DAY_OF_WEEK_LABELS[request.dayOfWeek]}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span className="truncate">{request.requestedByEmail}</span>
                    </div>

                    {request.tenantId && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        <span className="truncate text-xs">Tenant: {request.tenantId}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>
                        {format(request.createdAt, 'dd MMM yyyy à HH:mm', { locale: fr })}
                      </span>
                    </div>

                    {request.status === 'approved' && request.marketId && (
                      <div className="text-sm text-green-600">
                        Marché: {request.marketId}
                      </div>
                    )}

                    {request.status === 'pending' && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          onClick={() => openApprovalDialog(request)}
                          disabled={loading}
                          className="flex-1"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approuver
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openRejectDialog(request)}
                          disabled={loading}
                          className="flex-1"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onOpenChange={(open) => !open && setApprovalDialog(initialApprovalState)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Approuver la demande</DialogTitle>
          </DialogHeader>

          {approvalDialog.request && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{approvalDialog.request.city}</p>
                <p className="text-sm text-muted-foreground">
                  {DAY_OF_WEEK_LABELS[approvalDialog.request.dayOfWeek]} - Demandé par {approvalDialog.request.requestedByEmail}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Action</Label>
                <Select
                  value={approvalDialog.mode}
                  onValueChange={(v) =>
                    setApprovalDialog({ ...approvalDialog, mode: v as 'link' | 'create' })
                  }
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create">Créer un nouveau marché</SelectItem>
                    <SelectItem value="link" disabled={getMatchingMarkets().length === 0}>
                      Lier à un marché existant {getMatchingMarkets().length === 0 && '(aucun correspondant)'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {approvalDialog.mode === 'link' ? (
                <div className="space-y-2">
                  <Label>Marché existant</Label>
                  <Select
                    value={approvalDialog.existingMarketId}
                    onValueChange={(v) =>
                      setApprovalDialog({ ...approvalDialog, existingMarketId: v })
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un marché..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getMatchingMarkets().map((market) => (
                        <SelectItem key={market.id} value={market.id}>
                          {market.city} - {market.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Adresse du marché *</Label>
                    <Input
                      value={approvalDialog.newMarketAddress}
                      onChange={(e) =>
                        setApprovalDialog({ ...approvalDialog, newMarketAddress: e.target.value })
                      }
                      placeholder="Ex: Place du Marché, 69001 Lyon"
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Heure d'ouverture</Label>
                      <Input
                        type="time"
                        value={approvalDialog.newMarketOpeningTime}
                        onChange={(e) =>
                          setApprovalDialog({ ...approvalDialog, newMarketOpeningTime: e.target.value })
                        }
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Heure de fermeture</Label>
                      <Input
                        type="time"
                        value={approvalDialog.newMarketClosingTime}
                        onChange={(e) =>
                          setApprovalDialog({ ...approvalDialog, newMarketClosingTime: e.target.value })
                        }
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApprovalDialog(initialApprovalState)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button onClick={handleApprove} disabled={loading}>
              {loading ? 'Approbation...' : 'Approuver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => !open && setRejectDialog({ open: false, request: null, reason: '' })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeter la demande</DialogTitle>
          </DialogHeader>

          {rejectDialog.request && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{rejectDialog.request.city}</p>
                <p className="text-sm text-muted-foreground">
                  {DAY_OF_WEEK_LABELS[rejectDialog.request.dayOfWeek]} - Demandé par {rejectDialog.request.requestedByEmail}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Raison du rejet (optionnel)</Label>
                <Input
                  value={rejectDialog.reason}
                  onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                  placeholder="Ex: Ce marché n'accepte pas de nouveaux commerçants"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialog({ open: false, request: null, reason: '' })}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={loading}>
              {loading ? 'Rejet...' : 'Rejeter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
